import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints } from "../config/api";
import { getUserTypeMeta, userTypeForApi } from "../constants/userTypes";
import { UserTypePicker } from "./UserTypeControls";

/** If REACT_APP_USER_MANAGEMENT_ALLOWED_AUTHORITIES is unset, any logged-in user may use this panel. If set (comma-separated), authority must match a token (substring or exact, case-insensitive). */
function canUseUserManagement() {
  const raw = process.env.REACT_APP_USER_MANAGEMENT_ALLOWED_AUTHORITIES;
  if (raw == null || String(raw).trim() === "") return true;
  const authority = (localStorage.getItem("authority") || "").trim().toLowerCase();
  if (!authority) return false;
  return String(raw)
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .some((token) => authority === token || authority.includes(token));
}

const successBox = {
  color: "#047857",
  marginBottom: "16px",
  padding: "12px",
  backgroundColor: "#d1fae5",
  borderRadius: "6px",
  border: "1px solid #10b981",
  width: "100%",
  boxSizing: "border-box",
};

const errorBox = {
  color: "#b91c1c",
  marginBottom: "16px",
  padding: "12px",
  backgroundColor: "#fee2e2",
  borderRadius: "6px",
  border: "1px solid #ef4444",
  width: "100%",
  boxSizing: "border-box",
};

const sectionCard = {
  width: "100%",
  maxWidth: "520px",
  padding: "24px",
  borderRadius: "8px",
  border: "1px solid #c7d2fe",
  backgroundColor: "#fafbff",
  boxSizing: "border-box",
};

const OrgSelection = ({ onClose }) => {
  const [orgs, setOrgs] = useState([]);

  const [customEmail, setCustomEmail] = useState("");
  const [customSelectedOrg, setCustomSelectedOrg] = useState("");
  const [customNewOrgName, setCustomNewOrgName] = useState("");
  const [selectedUserType, setSelectedUserType] = useState("");
  const [customLoading, setCustomLoading] = useState(false);
  const [customSuccess, setCustomSuccess] = useState("");
  const [customError, setCustomError] = useState("");

  const allowed = useMemo(() => canUseUserManagement(), []);

  useEffect(() => {
    const fetchOrgs = async () => {
      if (!allowed) return;
      try {
        const response = await axios.get(endpoints.orgs, {
          headers: {
            ...getAuthHeaders(),
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        });
        setOrgs(response.data);
      } catch (error) {
        console.error("Error fetching orgs", error);
      }
    };

    fetchOrgs();
  }, [allowed]);

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    boxSizing: "border-box",
    color: "#1f2937",
    outline: "none",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  const buttonStyle = (loading) => ({
    width: "100%",
    padding: "12px 24px",
    fontSize: "14px",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: loading ? "not-allowed" : "pointer",
    fontWeight: "600",
    transition: "background-color 0.2s",
    opacity: loading ? 0.6 : 1,
  });

  const closeButtonStyle = {
    height: "40px",
    padding: "0 16px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    marginBottom: "20px",
    alignSelf: "flex-start",
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    setCustomSuccess("");
    setCustomError("");

    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      setCustomError("Please log in again.");
      return;
    }

    const sanitizedEmail = customEmail.toLowerCase().trim();
    if (!sanitizedEmail) {
      setCustomError("Enter an email address.");
      return;
    }
    if (!customNewOrgName.trim() && !customSelectedOrg) {
      setCustomError("Select an organization or enter a new organization name.");
      return;
    }

    setCustomLoading(true);
    try {
      let apiEndpoint = endpoints.authRegister;
      let payload;

      if (customNewOrgName.trim()) {
        payload = { email: sanitizedEmail, orgName: customNewOrgName.trim() };
        apiEndpoint = endpoints.createUser;
      } else {
        payload = { email: sanitizedEmail, organizationId: Number(customSelectedOrg) };
      }
      const apiUserType = userTypeForApi(selectedUserType);
      if (apiUserType) {
        payload.user_type = apiUserType;
      }

      await axios.post(apiEndpoint, payload, {
        headers: {
          ...authHeaders,
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });
      const typeMeta = getUserTypeMeta(selectedUserType);
      setCustomSuccess(
        selectedUserType
          ? `Registration sent as ${typeMeta.label}. ${typeMeta.description}`
          : "Registration sent. The user can complete sign-in with the existing email and password onboarding flow."
      );
      setCustomEmail("");
      setCustomSelectedOrg("");
      setCustomNewOrgName("");
      setSelectedUserType("");
    } catch (error) {
      console.error("Custom email register failed", error, error?.response);
      setCustomError(getErrorMessage(error, "Failed to register user."));
    } finally {
      setCustomLoading(false);
    }
  };

  if (!allowed) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        {onClose && (
          <button style={closeButtonStyle} type="button" onClick={onClose}>
            Close
          </button>
        )}
        <p style={{ color: "#b91c1c", fontSize: "16px", margin: 0 }}>
          You do not have permission to manage users. Ask an administrator to grant access, or set{" "}
          <code style={{ fontSize: "13px" }}>REACT_APP_USER_MANAGEMENT_ALLOWED_AUTHORITIES</code> to match your
          login <code style={{ fontSize: "13px" }}>authority</code> value.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        gap: "24px",
      }}
    >
      {onClose && (
        <button style={closeButtonStyle} type="button" onClick={onClose}>
          Close
        </button>
      )}

      <div>
        <h2
          style={{
            color: "#3730a3",
            fontSize: "26px",
            fontWeight: "bold",
            margin: 0,
            lineHeight: "1.2",
          }}
        >
          User management
        </h2>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "24px",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <div style={sectionCard}>
          <h3
            style={{
              color: "#312e81",
              fontSize: "18px",
              fontWeight: "700",
              margin: "0 0 12px 0",
            }}
          >
            Enter User Email and Select Organisation
          </h3>
          <p style={{ color: "#3730a3", margin: "0 0 12px 0", fontSize: "14px" }}>
            Note: it will work for Google as well as all other email domains.
          </p>

          {customSuccess && <div style={successBox}>{customSuccess}</div>}
          {customError && <div style={errorBox}>{customError}</div>}

          <form
            onSubmit={handleCustomSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%" }}
          >
            <input
              type="email"
              placeholder="user@company.com"
              value={customEmail}
              onChange={(e) => setCustomEmail(e.target.value)}
              required
              style={inputStyle}
            />

            <select
              value={customSelectedOrg}
              onChange={(e) => setCustomSelectedOrg(e.target.value)}
              disabled={customNewOrgName !== ""}
              style={selectStyle}
            >
              <option value="">Select organization</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>

            <p style={{ color: "#3730a3", fontWeight: "600", margin: "4px 0", textAlign: "center" }}>OR</p>

            <input
              type="text"
              placeholder="Create new organization"
              value={customNewOrgName}
              onChange={(e) => setCustomNewOrgName(e.target.value)}
              disabled={customSelectedOrg !== ""}
              style={inputStyle}
            />

            <UserTypePicker
              value={selectedUserType}
              onChange={setSelectedUserType}
              disabled={customLoading}
            />

            <button type="submit" disabled={customLoading} style={buttonStyle(customLoading)}>
              {customLoading ? "Submitting…" : "Register user (custom email)"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrgSelection;
